// Canonical Learning Event Store
// Multi-cert: events are cert-scoped via certPath()
// Events are IMMUTABLE — append-only, never update or delete

import { certPath, push, read } from './firebase-store.js';

/**
 * Create and persist a canonical learning event (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {object} eventData - { questionId, correct, responseMs, confidence, explanationMs, sessionPosition, sessionId, questionMeta }
 * @returns {string} eventId
 */
export async function createEvent(userId, certId, eventData) {
  const event = {
    timestamp: new Date().toISOString(),
    questionId: eventData.questionId,
    correct: eventData.correct,
    responseMs: eventData.responseMs,
    confidence: eventData.confidence || 'unsure',
    explanationMs: eventData.explanationMs || 0,
    sessionPosition: eventData.sessionPosition,
    sessionId: eventData.sessionId,
    questionMeta: eventData.questionMeta || {}
  };

  const eventId = await push(certPath(userId, certId, 'events'), event);
  return eventId;
}

/**
 * Get the N most recent events for a user (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {number} count
 * @returns {Array} events sorted newest-first
 */
export async function getRecentEvents(userId, certId, count) {
  const snap = await read(certPath(userId, certId, 'events'));
  if (!snap) return [];

  const events = Object.entries(snap)
    .map(([id, data]) => ({ eventId: id, ...data }))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, count);

  return events;
}

/**
 * Get all events for a specific session (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {string} sessionId
 * @returns {Array} events sorted by sessionPosition
 */
export async function getEventsBySession(userId, certId, sessionId) {
  const snap = await read(certPath(userId, certId, 'events'));
  if (!snap) return [];

  return Object.entries(snap)
    .map(([id, data]) => ({ eventId: id, ...data }))
    .filter(e => e.sessionId === sessionId)
    .sort((a, b) => a.sessionPosition - b.sessionPosition);
}
